import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Target, Users, Palette, Timer, Trophy, Zap, Volume2, Share2 } from "lucide-react";
import SEOFooter from "@/components/SEOFooter";

export default function HowToPlay() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-card to-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Game
            </Button>
          </Link>
          <h1 className="text-xl font-bold text-primary">67 CHAOS</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Hero Section */}
          <section className="text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight">
              How to Play <span className="text-primary">67 CHAOS</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Master the viral party game that's taking the internet by storm! Learn the rules, 
              strategies, and tips to hit exactly 67 with your friends.
            </p>
          </section>

          {/* Quick Start */}
          <Card className="p-6 bg-primary/10 border-primary/30">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Zap className="h-6 w-6 text-primary" />
              Quick Start Guide
            </h2>
            <ol className="space-y-3 text-lg">
              <li className="flex items-start gap-3">
                <span className="font-bold text-primary min-w-[24px]">1.</span>
                <span>Enter your name and create a room or join with a code</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-bold text-primary min-w-[24px]">2.</span>
                <span>Share the room code with 1-7 friends</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-bold text-primary min-w-[24px]">3.</span>
                <span>Each round, pick a number from your two options</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-bold text-primary min-w-[24px]">4.</span>
                <span>Work together to hit EXACTLY 67 to win!</span>
              </li>
            </ol>
          </Card>

          {/* Core Mechanics */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Target className="h-8 w-8 text-primary" />
              The Goal: Hit Exactly 67
            </h2>
            <p className="text-lg text-muted-foreground">
              67 CHAOS is a cooperative multiplayer game where players must collectively reach 
              a total of exactly 67 points. Each round, every player secretly selects a number, 
              and all selections are added to the running total. The challenge? You can't see 
              what others are picking in real-time, and you must hit 67 precisely — not 66, not 68!
            </p>
            <p className="text-lg text-muted-foreground">
              The game is inspired by the viral "67" meme that swept Gen Alpha social media, 
              representing collective chaos and teamwork. It was even recognized as 
              Dictionary.com's 2025 Word of the Year!
            </p>
          </section>

          {/* Color System */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Palette className="h-8 w-8 text-primary" />
              The Color Guide System
            </h2>
            <p className="text-lg text-muted-foreground mb-4">
              Since you can't see the exact total (that would be too easy!), the background 
              color gives you hints about how close you are to 67:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-4 bg-destructive/20 border-destructive/50">
                <h3 className="font-bold text-destructive text-lg">🔴 Red Zone</h3>
                <p className="text-muted-foreground">Far from 67 — you need bigger moves!</p>
              </Card>
              <Card className="p-4 bg-yellow-500/20 border-yellow-500/50">
                <h3 className="font-bold text-yellow-500 text-lg">🟡 Yellow Zone</h3>
                <p className="text-muted-foreground">Getting warmer — be more careful now</p>
              </Card>
              <Card className="p-4 bg-orange-500/20 border-orange-500/50">
                <h3 className="font-bold text-orange-500 text-lg">🟠 Orange Zone</h3>
                <p className="text-muted-foreground">Very close — one wrong move could overshoot!</p>
              </Card>
              <Card className="p-4 bg-primary/20 border-primary/50">
                <h3 className="font-bold text-primary text-lg">🟢 Green Zone</h3>
                <p className="text-muted-foreground">You've hit 67! Victory!</p>
              </Card>
            </div>
          </section>

          {/* Multiplayer */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              Multiplayer Gameplay
            </h2>
            <p className="text-lg text-muted-foreground">
              67 CHAOS shines as a multiplayer experience! Here's how it works:
            </p>
            <ul className="space-y-3 text-lg text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span><strong>2-8 Players:</strong> Best with 3-6 for optimal chaos</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span><strong>Real-time:</strong> Everyone picks simultaneously — no waiting!</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span><strong>3-Second Timer:</strong> Pick fast or a random number is chosen for you</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span><strong>Room Codes:</strong> Share via WhatsApp, SMS, or copy the link</span>
              </li>
            </ul>
          </section>

          {/* Timer */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Timer className="h-8 w-8 text-primary" />
              The 3-Second Rule
            </h2>
            <p className="text-lg text-muted-foreground">
              Each round, you have just 3 seconds to make your choice. This creates 
              pulse-pounding moments where split-second decisions matter. If you don't 
              pick in time, the game randomly selects one of your numbers — which could 
              lead to disaster (or accidental genius)!
            </p>
          </section>

          {/* Winning & Losing */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Trophy className="h-8 w-8 text-primary" />
              Winning & Losing
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-4 bg-primary/10 border-primary/30">
                <h3 className="font-bold text-primary text-xl mb-2">🎉 You Win When...</h3>
                <p className="text-muted-foreground">
                  The total hits exactly 67! Confetti flies, the victory animation plays, 
                  and you earn XP based on how efficiently you reached the target.
                </p>
              </Card>
              <Card className="p-4 bg-destructive/10 border-destructive/30">
                <h3 className="font-bold text-destructive text-xl mb-2">💀 You Lose When...</h3>
                <p className="text-muted-foreground">
                  After 10 rounds without hitting 67, the game ends. But don't worry — 
                  you can rematch instantly and try again!
                </p>
              </Card>
            </div>
          </section>

          {/* Pro Tips */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold">💡 Pro Tips & Strategies</h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                <strong>1. Watch the colors:</strong> The background shift is your best friend. 
                When it turns orange, switch to smaller numbers.
              </p>
              <p>
                <strong>2. Communicate (if you can):</strong> If playing in the same room, 
                quick verbal signals like "going small" can prevent overshooting.
              </p>
              <p>
                <strong>3. Later rounds get strategic:</strong> After round 4, number 
                options become more calculated based on your distance to 67.
              </p>
              <p>
                <strong>4. Practice solo first:</strong> Use Practice Mode to understand 
                the mechanics before joining friends.
              </p>
              <p>
                <strong>5. Recovery is possible:</strong> If you overshoot, negative 
                numbers may appear in later rounds to help you get back to 67.
              </p>
            </div>
          </section>

          {/* Sound & Effects */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Volume2 className="h-8 w-8 text-primary" />
              Immersive Audio Experience
            </h2>
            <p className="text-lg text-muted-foreground">
              67 CHAOS features dynamic audio that responds to gameplay. The background 
              music intensifies as you approach 67, tension sounds play during close calls, 
              and satisfying effects accompany your selections. On mobile, haptic feedback 
              adds another layer of immersion!
            </p>
          </section>

          {/* Sharing */}
          <section className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Share2 className="h-8 w-8 text-primary" />
              Share Your Wins
            </h2>
            <p className="text-lg text-muted-foreground">
              After each game, generate a shareable result card showing your final score, 
              rounds played, and MVP status. Share directly to Twitter, TikTok, or save 
              the image to flex your 67 CHAOS skills!
            </p>
          </section>

          {/* CTA */}
          <section className="text-center py-8">
            <Link to="/">
              <Button size="lg" className="h-16 px-12 text-xl font-bold glow-green">
                Play 67 CHAOS Now — It's Free!
              </Button>
            </Link>
          </section>
        </motion.article>
      </main>

      <SEOFooter />
    </div>
  );
}
